import React, { useCallback, useEffect, useState } from 'react';
import useRefreshOnVisible from '../hooks/useRefreshOnVisible';
import { useFeedbackScreen } from '../utils/feedbackScreen';
import { useParams, useNavigate } from 'react-router-dom';
import { getEvent, updateEvent, checkEventPublicationExists, publishEventToChat, getAccessibleChats, registerSelf, unregisterSelf } from '../api/eventsApi';
import { getEventTournaments, deleteTournament } from '../api/tournamentsApi';
import { updateMyTemplateDefaultChat } from '../api/templatesApi';
import { useToast } from '../components/Toast';
import { Spinner, PageError, formatDateDisplay } from '../components/UI';
import EventForm from '../components/EventForm';
import RegistrationRow from '../components/RegistrationRow';
import RolesPanel from '../components/RolesPanel';
import AddParticipantPanel from '../components/AddParticipantPanel';

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconEdit = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconArrowLeft = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const IconChevron = ({ open }) => (
  <svg
    width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
  >
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// Значок для баннера «у вас есть права на управление».
const IconShield = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

// Значок для полосы «вы видите страницу как обычный участник».
const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

// ─── Tournament helpers ───────────────────────────────────────────────────────

const SPORT_LABELS = {
  PICKLEBALL:       'Пиклбол',
  PADEL:            'Падел',
  TENNIS:           'Теннис',
  BADMINTON:        'Бадминтон',
  BEACH_VOLLEYBALL: 'Пляжный волейбол',
  SQUASH:           'Сквош',
  TABLE_TENNIS:     'Настольный теннис',
};

const FORMAT_LABELS = {
  AMERICANO:    'Americano',
  MEXICANO:     'Mexicano',
  BEAT_THE_BOX: 'Beat the Box',
  KING_OF_COURT:'King of the Court',
  ROUND_ROBIN:  'Round Robin',
};

const TOURNAMENT_STATUS_CONFIG = {
  DRAFT:     { label: 'Черновик',  bg: '#f3f4f6', border: '#d1d5db', color: '#6b7280' },
  RUNNING:   { label: 'Идёт',      bg: '#dcfce7', border: '#86efac', color: '#15803d' },
  FINISHED:  { label: 'Завершён',  bg: '#ede9fe', border: '#c4b5fd', color: '#6d28d9' },
  CANCELLED: { label: 'Отменён',   bg: '#fee2e2', border: '#fca5a5', color: '#b91c1c' },
};

const TournamentStatusBadge = ({ status }) => {
  const cfg = TOURNAMENT_STATUS_CONFIG[status] || TOURNAMENT_STATUS_CONFIG.DRAFT;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 99,
      background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color,
      flexShrink: 0,
    }}>
      {cfg.label}
    </span>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const ViewEventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [participantEditMode, setParticipantEditMode] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [selfLoading, setSelfLoading] = useState(false);
  const [showUnregisterConfirm, setShowUnregisterConfirm] = useState(false);

  // ── Предпросмотр «как обычный участник» ─────────────────────────────────────
  // Чисто фронтовая симуляция: подменяет canEdit/canManageAttendance/canManageTournament
  // на false в effectiveEvent (см. ниже), НЕ трогая реальные данные/права. Нужна тем, у кого
  // есть права на событие — посмотреть, что видит обычный участник, не выходя из своего аккаунта.
  const [previewAsParticipant, setPreviewAsParticipant] = useState(false);

  // Турниры
  const [tournaments, setTournaments] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(false);
  const [tournamentEditMode, setTournamentEditMode] = useState(false);

  // Публикация события
  const [hasPublications, setHasPublications] = useState(false);       // глобально (для лейбла кнопки в edit-режиме)
  const [hasPublicationsForChat, setHasPublicationsForChat] = useState(false); // per-chat (для блока публикации)
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishChatId, setPublishChatId] = useState(null);
  const [accessibleChats, setAccessibleChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [showRepublishConfirm, setShowRepublishConfirm] = useState(false);

  const fetchEvent = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const data = await getEvent(eventId);
      setEvent(data);
      setRegistrations(data.registrations || []);
    } catch (err) {
      setPageError(err.message || 'Не удалось загрузить событие');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  // После загрузки события — глобальная проверка публикаций и загрузка списка чатов (для редакторов)
  useEffect(() => {
    if (!event?.publicId || !event?.canEdit) return;

    // Глобальная проверка: публиковалось ли событие хоть куда-нибудь (для лейбла в edit-режиме)
    checkEventPublicationExists(event.publicId)
      .then(r => setHasPublications(r.exists))
      .catch(() => {}); // некритично

    setChatsLoading(true);
    getAccessibleChats()
      .then(response => {
        setAccessibleChats(response.chats || []);
        // Предустанавливаем publishChatId из сохранённого дефолта пользователя
        if (response.defaultChatId != null) setPublishChatId(response.defaultChatId);
      })
      .catch(err => console.warn('Failed to load accessible chats:', err))
      .finally(() => setChatsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.publicId, event?.canEdit]);

  // Загрузка турниров — только когда фича включена в org
  useEffect(() => {
    if (!event?.publicId || !event?.tournamentsEnabled) return;
    setTournamentsLoading(true);
    getEventTournaments(event.publicId)
      .then(data => setTournaments(data || []))
      .catch(err => console.warn('Failed to load tournaments:', err))
      .finally(() => setTournamentsLoading(false));
  }, [event?.publicId, event?.tournamentsEnabled]);

  // Per-chat проверка: публиковалось ли событие именно в выбранный чат.
  // Пересчитывается каждый раз при смене publishChatId.
  // publishChatId=null → личный чат (передаём chatId=0, бэкенд резолвит в userId)
  useEffect(() => {
    if (!event?.publicId || !event?.canEdit) return;
    const chatIdParam = publishChatId ?? 0; // 0 = личный чат (sentinel для бэкенда)
    checkEventPublicationExists(event.publicId, chatIdParam)
      .then(r => setHasPublicationsForChat(r.exists))
      .catch(() => {});
  }, [event?.publicId, event?.canEdit, publishChatId]);

  const handleDeleteTournament = async (publicId, e) => {
    e.stopPropagation();
    if (!window.confirm('Удалить этот турнир? Действие нельзя отменить.')) return;
    try {
      await deleteTournament(publicId);
      setTournaments(prev => prev.filter(t => t.publicId !== publicId));
      addToast('Турнир удалён', 'success');
    } catch (err) {
      addToast(err.message || 'Не удалось удалить турнир', 'error');
    }
  };

  const handleUpdate = async (values) => {
    setSaving(true);
    try {
      const updated = await updateEvent(event.publicId, values);
      setEvent(updated);
      setRegistrations(updated.registrations || registrations);
      setEditMode(false);
      addToast('Событие успешно обновлено', 'success');
    } catch (err) {
      addToast(`Ошибка сохранения: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Тихое обновление списка участников — без полного спиннера
  const refreshRegistrations = useCallback(async () => {
    try {
      const data = await getEvent(eventId);
      setEvent(data);
      setRegistrations(data.registrations || []);
    } catch (err) {
      addToast(`Не удалось обновить список: ${err.message}`, 'error');
    }
  }, [eventId, addToast]);

  const handleParticipantsChanged = useCallback(async () => {
    await refreshRegistrations();
    setParticipantEditMode(false);
  }, [refreshRegistrations]);

  // Обновляем событие при возврате на свёрнутую страницу (если данные могли устареть).
  // Отключено в режимах редактирования, чтобы не затирать несохранённые изменения.
  useRefreshOnVisible(refreshRegistrations, {
    enabled: !editMode && !participantEditMode,
  });

  // Самостоятельная запись / отмена записи (дубль логики телеграм-бота, только сам себя).
  // Эндпоинты возвращают обновлённое событие — применяем его без дополнительного запроса.
  const doSelfAction = useCallback(async (register) => {
    if (!event) return;
    setSelfLoading(true);
    try {
      const updated = register
        ? await registerSelf(event.publicId)
        : await unregisterSelf(event.publicId);
      setEvent(updated);
      setRegistrations(updated.registrations || []);
      addToast(register ? 'Вы записаны!' : 'Запись отменена', 'success');
    } catch (err) {
      const fallback = register ? 'Не удалось записаться' : 'Не удалось отменить запись';
      addToast(err.message || fallback, 'error');
    } finally {
      setSelfLoading(false);
    }
  }, [event, addToast]);

  // Запись — сразу; отмена — через подтверждение во всплывающем окне.
  const handleSelfToggle = useCallback(() => {
    if (!event) return;
    if (event.selfRegistrationStatus) {
      setShowUnregisterConfirm(true);
    } else {
      doSelfAction(true);
    }
  }, [event, doSelfAction]);

  // Фактическая публикация — вызывается напрямую (первичная) или после подтверждения (повторная)
  const doPublish = async () => {
    setPublishLoading(true);
    try {
      await publishEventToChat(event.publicId, publishChatId ?? null);
      setHasPublications(true);
      setHasPublicationsForChat(true);
      addToast(hasPublicationsForChat ? 'Событие переопубликовано!' : 'Событие опубликовано!', 'success');
    } catch (err) {
      addToast(`Ошибка публикации: ${err.message}`, 'error');
    } finally {
      setPublishLoading(false);
    }
  };

  // Нажатие кнопки: если уже публиковалось в этот чат — показываем предупреждение
  const handlePublish = () => {
    if (hasPublicationsForChat) {
      setShowRepublishConfirm(true);
    } else {
      doPublish();
    }
  };

  // Смена выбранного чата для публикации + сохранение дефолта
  const handlePublishChatChange = (newChatId) => {
    setPublishChatId(newChatId ? Number(newChatId) : null);
    updateMyTemplateDefaultChat(newChatId ? Number(newChatId) : null)
      .catch(err => console.warn('Failed to save default chat:', err));
  };

  // Ярлык экрана для контекста обратной связи: URL события одинаковый, но UI зависит
  // от режима (просмотр / редактирование / правка состава участников / предпросмотр участника).
  useFeedbackScreen(
    'Событие · ' + (
      previewAsParticipant ? 'предпросмотр участника'
        : editMode ? 'редактирование'
        : participantEditMode ? 'правка состава'
        : 'просмотр'
    )
  );

  if (loading) return <Spinner text="Загрузка события..." />;
  if (pageError) return <PageError message={pageError} onRetry={fetchEvent} />;
  if (!event) return null;

  // ── effectiveEvent — то, что реально читает вся разметка ниже ──────────────
  // В обычном режиме это тот же объект event. В предпросмотре участника —
  // копия с canEdit/canManageAttendance/canManageTournament принудительно false,
  // как будто бэк их не выдал. Реальные права (event.*) не меняются нигде —
  // предпросмотр не может по ошибке отключить/включить настоящий доступ.
  const effectiveEvent = previewAsParticipant
    ? { ...event, canEdit: false, canManageAttendance: false, canManageTournament: false }
    : event;

  const enterParticipantPreview = () => {
    setEditMode(false);
    setParticipantEditMode(false);
    setTournamentEditMode(false);
    setRolesOpen(false);
    setPreviewAsParticipant(true);
  };
  const exitParticipantPreview = () => setPreviewAsParticipant(false);

  // Есть ли у пользователя хоть одно из прав на событие — считаем от реальных
  // event.can*, а не effectiveEvent, чтобы баннер не пропадал, пока листаем предпросмотр.
  const hasAnyRights = event.canEdit || event.canManageAttendance || event.canManageTournament;

  return (
    <div style={{ height: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>

      {/* ── Header ── */}
      <div style={{
        background: editMode
          ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
          : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        padding: '10px 16px',
        color: '#fff',
        transition: 'background 0.4s',
        display: 'flex', alignItems: 'center',
        flexShrink: 0,
      }}>
        {editMode ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => setEditMode(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 8, padding: '5px 10px',
                color: '#fff', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', backdropFilter: 'blur(4px)',
              }}
            >
              <IconArrowLeft />
              Назад
            </button>
            <span style={{
              fontSize: 12, fontWeight: 700, letterSpacing: 0.6,
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 6, padding: '2px 8px', textTransform: 'uppercase',
            }}>
              Редактирование
            </span>
          </div>
        ) : effectiveEvent.canEdit && (
          <button
            onClick={() => navigate('/events')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8, padding: '5px 12px',
              color: '#fff', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', backdropFilter: 'blur(4px)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
            Все события
          </button>
        )}
      </div>

      <div style={{ padding: '14px 16px 16px', flex: 1, overflowY: 'auto' }}>

        {/* ── Edit form ── */}
        {editMode ? (
          <div style={{
            background: '#fff', borderRadius: 16,
            padding: '20px 16px',
            boxShadow: '0 1px 12px rgba(0,0,0,0.07)',
          }}>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
              Измените данные и нажмите «Сохранить»
            </p>
            <EventForm
              initialValues={event}
              onSubmit={handleUpdate}
              submitLabel={hasPublications ? 'Сохранить и переопубликовать' : 'Сохранить изменения'}
              loading={saving}
              onCancel={() => setEditMode(false)}
            />
          </div>
        ) : (
          <>
            {/* ── Полоса предпросмотра «как обычный участник» ── */}
            {previewAsParticipant && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                background: '#f3f4f6', borderRadius: 12,
                border: '1.5px solid #d1d5db',
                padding: '9px 14px', marginBottom: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ color: '#6b7280', flexShrink: 0, display: 'flex' }}><IconEye /></span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
                    Вы видите страницу как обычный участник
                  </span>
                </div>
                <button
                  onClick={exitParticipantPreview}
                  style={{
                    flexShrink: 0,
                    padding: '6px 12px', borderRadius: 8, border: 'none',
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Вернуться
                </button>
              </div>
            )}

            {/* ── Баннер прав — почему видны админ-кнопки, и ссылка на предпросмотр ── */}
            {!previewAsParticipant && hasAnyRights && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: '#eef2ff', borderRadius: 12,
                border: '1.5px solid #c7d2fe',
                padding: '11px 14px', marginBottom: 12,
              }}>
                <span style={{ color: '#6366f1', flexShrink: 0, display: 'flex', marginTop: 1 }}><IconShield /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: '#3730a3', lineHeight: 1.5 }}>
                    Вы видите это событие с дополнительными правами
                  </div>
                  <button
                    onClick={enterParticipantPreview}
                    style={{
                      marginTop: 8,
                      padding: '6px 12px', borderRadius: 8,
                      border: '1.5px solid #c7d2fe', background: '#fff',
                      color: '#4f46e5', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Посмотреть как участник
                  </button>
                </div>
              </div>
            )}

            {/* ── Compact stats row ── */}
            {(() => {
              const confirmed = registrations.filter(r => r.status !== 'WAITING');
              const waiting = registrations.filter(r => r.status === 'WAITING');
              const hasLimit = event.totalSlots != null;
              return (
                <div style={{
                  background: '#fff', borderRadius: 12,
                  padding: '10px 14px', marginBottom: 12,
                  border: '1.5px solid #f3f4f6',
                  display: 'flex', alignItems: 'center', gap: 8,
                  flexWrap: 'wrap',
                }}>
                  {/* Confirmed / limit */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#374151' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    <span style={{ fontWeight: 600, color: '#374151' }}>
                      {hasLimit ? 'Подтверждено:' : 'Записалось:'}
                    </span>
                    <span style={{
                      fontWeight: 800, color: '#6366f1',
                      background: '#ede9fe', borderRadius: 99, padding: '1px 10px', fontSize: 14,
                    }}>
                      {hasLimit ? `${confirmed.length} / ${event.totalSlots}` : registrations.length}
                    </span>
                  </div>

                  {/* Waiting count — only when waitingList enabled and has waiters */}
                  {waiting.length > 0 && (
                    <>
                      <div style={{ width: 1, height: 16, background: '#e5e7eb' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#374151' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        <span style={{ fontWeight: 600, color: '#374151' }}>В очереди:</span>
                        <span style={{
                          fontWeight: 800, color: '#d97706',
                          background: '#fef3c7', borderRadius: 99, padding: '1px 10px', fontSize: 14,
                        }}>
                          {waiting.length}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* ── Объединённая карточка: дата + описание + режим мест ── */}
            {(event.eventDate || event.totalSlots != null || event.description || effectiveEvent.canEdit) && (
              <div style={{
                background: '#fff', borderRadius: 14,
                boxShadow: '0 1px 8px rgba(0,0,0,0.05)', marginBottom: 12,
                padding: '14px 16px',
                position: 'relative',
              }}>
                {/* Маленькая кнопка-иконка — абсолютно в правом верхнем углу */}
                {effectiveEvent.canEdit && (
                  <button
                    onClick={() => setEditMode(true)}
                    title="Редактировать"
                    style={{
                      position: 'absolute', top: 10, right: 10,
                      width: 30, height: 30,
                      background: '#f5f3ff', border: '1.5px solid #e0e7ff',
                      borderRadius: 8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: '#6366f1',
                      transition: 'background 0.15s', flexShrink: 0,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#ede9fe'}
                    onMouseLeave={e => e.currentTarget.style.background = '#f5f3ff'}
                  >
                    <IconEdit />
                  </button>
                )}

                {/* 1. Дата — только если заполнена */}
                {(() => {
                  const display = formatDateDisplay(event.eventDate, event.eventEndDate);
                  if (!display) return null;
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      marginBottom: (event.description || event.totalSlots != null) ? 12 : 0,
                      paddingRight: effectiveEvent.canEdit ? 36 : 0,
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                        background: '#ede9fe',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/>
                          <line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{display.date}</div>
                        {display.time && <div style={{ fontSize: 12, color: '#6366f1', fontWeight: 600 }}>{display.time}</div>}
                      </div>
                    </div>
                  );
                })()}

                {/* 2. Описание */}
                {(event.description || effectiveEvent.canEdit) && (
                  <div style={{
                    marginBottom: event.totalSlots != null ? 12 : 0,
                    paddingRight: effectiveEvent.canEdit ? 36 : 0,
                  }}>
                    {event.description ? (
                      <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
                        {event.description}
                      </p>
                    ) : (
                      <p style={{ margin: 0, fontSize: 14, color: '#9ca3af', fontStyle: 'italic' }}>
                        Описание не добавлено
                      </p>
                    )}
                  </div>
                )}

                {/* 3. Режим листа ожидания — только если задан лимит */}
                {event.totalSlots != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: 12, fontWeight: 600,
                      color: event.waitingListEnabled ? '#15803d' : '#4f46e5',
                    }}>
                      {event.waitingListEnabled ? '📋 Лист ожидания включён' : '🔒 Без листа ожидания'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Блок публикации — только для canEdit ── */}
            {effectiveEvent.canEdit && (
              <div style={{
                background: '#fff', borderRadius: 14,
                boxShadow: '0 1px 8px rgba(0,0,0,0.05)', marginBottom: 12,
                padding: '14px 16px',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
                  {hasPublicationsForChat ? 'Повторно опубликовать' : 'Опубликовать событие'}
                </div>

                {/* Селектор чата */}
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  {/* Иконка чата слева */}
                  <div style={{
                    position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <select
                    value={chatsLoading ? '' : (publishChatId ?? '')}
                    onChange={e => handlePublishChatChange(e.target.value || null)}
                    disabled={chatsLoading || publishLoading}
                    style={{
                      width: '100%',
                      padding: '9px 10px 9px 32px',
                      borderRadius: 10,
                      border: `1.5px solid ${chatsLoading ? '#e5e7eb' : '#a5b4fc'}`,
                      fontSize: 14,
                      color: chatsLoading ? '#9ca3af' : '#111827',
                      background: chatsLoading ? '#f3f4f6' : '#fafafe',
                      outline: 'none',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      cursor: (chatsLoading || publishLoading) ? 'wait' : 'pointer',
                    }}
                  >
                    {chatsLoading ? (
                      <option value="">Загрузка чатов...</option>
                    ) : (
                      <>
                        <option value="">Личный чат с ботом</option>
                        {accessibleChats.length === 0 && (
                          <option value="" disabled style={{ color: '#9ca3af' }}>
                            — Добавьте бота в группу, чтобы публиковать туда
                          </option>
                        )}
                        {accessibleChats.map(chat => (
                          <option key={chat.telegramChatId} value={chat.telegramChatId}>
                            {chat.title || `Чат ${chat.telegramChatId}`}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {/* Стрелка вниз */}
                  <div style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </div>

                {/* Кнопка публикации */}
                <button
                  onClick={handlePublish}
                  disabled={chatsLoading || publishLoading}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: publishLoading || chatsLoading
                      ? '#e0e7ff'
                      : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    color: publishLoading || chatsLoading ? '#6366f1' : '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: (publishLoading || chatsLoading) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'all 0.15s',
                  }}
                >
                  {publishLoading ? (
                    'Публикация...'
                  ) : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                      {hasPublicationsForChat ? 'Повторно опубликовать' : 'Опубликовать'}
                    </>
                  )}
                </button>
              </div>
            )}


            {/* ── Participants block ── */}
            <div style={{
              background: '#fff', borderRadius: 14, padding: '14px 10px',
              boxShadow: '0 1px 8px rgba(0,0,0,0.05)', marginBottom: 12,
            }}>

              {/* Header row */}
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>
                    Участники
                  </h2>
                </div>

                {effectiveEvent.canManageAttendance && (
                  <button
                    onClick={() => setParticipantEditMode(v => !v)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '5px 12px', borderRadius: 8,
                      border: `1.5px solid ${participantEditMode ? '#86efac' : '#e0e7ff'}`,
                      background: participantEditMode ? '#dcfce7' : '#f5f3ff',
                      color: participantEditMode ? '#16a34a' : '#6366f1',
                      fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {participantEditMode
                      ? <><IconArrowLeft /> Вернуться к посещаемости</>
                      : <><IconEdit /> Изменить список</>
                    }
                  </button>
                )}
              </div>

              {/* Предупреждение о переполнении при жёстком ограничении —
                  показываем и при обычном просмотре, и в режиме редактирования */}
              {effectiveEvent.canManageAttendance && event.totalSlots != null && !event.waitingListEnabled && (() => {
                const confirmedCount = registrations.filter(r => r.status !== 'WAITING').length;
                const isFull = confirmedCount >= event.totalSlots;
                return isFull ? (
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 12px', marginBottom: 8,
                    background: '#fffbeb', borderRadius: 10,
                    border: '1.5px solid #fde68a',
                  }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
                    <span style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                      <strong>Событие заполнено ({confirmedCount}/{event.totalSlots}).</strong>{' '}
                      Вы обладаете правами добавить участника сверх лимита.
                    </span>
                  </div>
                ) : null;
              })()}

              {/* Add participant panel — ВЫШЕ списка в режиме редактирования */}
              {effectiveEvent.canManageAttendance && participantEditMode && (
                <AddParticipantPanel
                  eventId={event.publicId}
                  organizationId={event.organizationId}
                  onAdded={handleParticipantsChanged}
                />
              )}

              {/* Participants list */}
              {registrations.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                  Пока никто не записался
                </div>
              ) : (() => {
                const confirmed = registrations.filter(r => r.status !== 'WAITING');
                const waiting = registrations.filter(r => r.status === 'WAITING');
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Confirmed participants */}
                    {confirmed.map((reg, idx) => (
                      <RegistrationRow
                        key={reg.registrationId}
                        registration={reg}
                        index={idx}
                        canManage={effectiveEvent.canManageAttendance}
                        editMode={participantEditMode}
                        eventId={event.publicId}
                        onDeleted={handleParticipantsChanged}
                        onChanged={handleParticipantsChanged}
                      />
                    ))}

                    {/* Waiting list section */}
                    {waiting.length > 0 && (
                      <>
                        {/* Section divider */}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          margin: '4px 0',
                        }}>
                          <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, #fde68a, transparent)' }} />
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '3px 10px', borderRadius: 99,
                            background: '#fef3c7', border: '1px solid #fde68a',
                          }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="12 6 12 12 16 14"/>
                            </svg>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#d97706' }}>
                              Лист ожидания · {waiting.length}
                            </span>
                          </div>
                          <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, #fde68a)' }} />
                        </div>

                        {waiting.map((reg, idx) => (
                          <RegistrationRow
                            key={reg.registrationId}
                            registration={reg}
                            index={idx}
                            canManage={effectiveEvent.canManageAttendance}
                            editMode={participantEditMode}
                            eventId={event.publicId}
                            onDeleted={handleParticipantsChanged}
                            onChanged={handleParticipantsChanged}
                          />
                        ))}
                      </>
                    )}
                  </div>
                );
              })()}

            </div>

            {/* ── Блок «Турниры» — только когда включено в настройках org ── */}
            {/* Скрываем если нет прав на управление турниром И турниры ещё не загрузились или их нет */}
            {event.tournamentsEnabled && (effectiveEvent.canManageTournament || (!tournamentsLoading && tournaments.length > 0)) && (
              <div style={{
                background: '#fff', borderRadius: 14,
                boxShadow: '0 1px 8px rgba(0,0,0,0.05)', marginBottom: 12,
                overflow: 'hidden',
              }}>
                {/* Заголовок */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px',
                  borderBottom: (tournamentsLoading || tournaments.length > 0) ? '1px solid #f0f0f5' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Иконка трофея */}
                    <div style={{
                      width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                      background: '#fef3c7',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="8 21 12 17 16 21"/>
                        <line x1="12" y1="17" x2="12" y2="11"/>
                        <path d="M7 4V3H17V4"/>
                        <path d="M7 4c0 5 5 8 5 8s5-3 5-8"/>
                        <path d="M5 4H3c0 5 2 7 4 8"/>
                        <path d="M19 4h2c0 5-2 7-4 8"/>
                      </svg>
                    </div>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>
                      Турнир
                    </h2>
                    {event.sport && (
                      <span style={{
                        fontSize: 11, fontWeight: 600,
                        background: '#fef3c7', color: '#92400e',
                        border: '1px solid #fde68a',
                        borderRadius: 99, padding: '2px 8px',
                      }}>
                        {SPORT_LABELS[event.sport] || event.sport}
                      </span>
                    )}
                  </div>

                  {/* Кнопка «Создать» (нет турниров) или «Изменить» (есть турниры) */}
                  {effectiveEvent.canManageTournament && !tournamentsLoading && (
                    tournaments.length === 0 ? (
                      <button
                        onClick={() => navigate(`/tournaments/new?eventId=${event.publicId}${event.sport ? `&sport=${event.sport}` : ''}`)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '5px 12px', borderRadius: 8,
                          border: '1.5px solid #fde68a',
                          background: '#fef3c7',
                          color: '#92400e',
                          fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        Создать
                      </button>
                    ) : (
                      <button
                        onClick={() => setTournamentEditMode(v => !v)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '5px 12px', borderRadius: 8,
                          border: `1.5px solid ${tournamentEditMode ? '#86efac' : '#e0e7ff'}`,
                          background: tournamentEditMode ? '#dcfce7' : '#f5f3ff',
                          color: tournamentEditMode ? '#16a34a' : '#6366f1',
                          fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {tournamentEditMode ? (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="15 18 9 12 15 6"/>
                            </svg>
                            Готово
                          </>
                        ) : (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                            Изменить
                          </>
                        )}
                      </button>
                    )
                  )}
                </div>

                {/* Содержимое */}
                {tournamentsLoading ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    Загрузка...
                  </div>
                ) : tournaments.length === 0 ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    {effectiveEvent.canManageTournament
                      ? 'Создайте турнир для этого события'
                      : 'Турниров пока нет'}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {tournaments.map((t, idx) => (
                      <button
                        key={t.publicId}
                        onClick={() => { if (!tournamentEditMode) navigate(`/tournaments/${t.publicId}`); }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 16px',
                          background: 'none', border: 'none',
                          borderTop: idx > 0 ? '1px solid #f0f0f5' : 'none',
                          cursor: tournamentEditMode ? 'default' : 'pointer', textAlign: 'left',
                          width: '100%',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { if (!tournamentEditMode) e.currentTarget.style.background = '#fafafa'; }}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                              {FORMAT_LABELS[t.format] || t.format}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                              {t.courtsCount} {t.courtsCount === 1 ? 'корт' : t.courtsCount < 5 ? 'корта' : 'кортов'}
                              {' · '}
                              {t.playersCount ?? 0} игроков
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <TournamentStatusBadge status={t.status} />
                          {tournamentEditMode && effectiveEvent.canManageTournament && t.status !== 'FINISHED' && (
                            <button
                              onClick={(e) => handleDeleteTournament(t.publicId, e)}
                              title="Удалить турнир"
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 36, height: 36, borderRadius: 8,
                                background: '#fee2e2', border: 'none',
                                cursor: 'pointer', color: '#ef4444',
                                flexShrink: 0,
                                transition: 'color 0.1s, background 0.1s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#fecaca'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#fee2e2'; }}
                            >
                              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            </button>
                          )}
                          {!tournamentEditMode && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                          )}
                        </div>
                      </button>
                    ))}

                    {/* Кнопка «Добавить ещё» — когда есть турниры и пользователь может управлять */}
                    {effectiveEvent.canManageTournament && (
                      <button
                        onClick={() => navigate(`/tournaments/new?eventId=${event.publicId}${event.sport ? `&sport=${event.sport}` : ''}`)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          padding: '10px 16px',
                          background: 'none', border: 'none',
                          borderTop: '1px solid #f0f0f5',
                          cursor: 'pointer',
                          fontSize: 13, fontWeight: 600, color: '#6366f1',
                          width: '100%',
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        Ещё один турнир
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Рекламка турниров — когда модуль не включён в настройках org ── */}
            {/* Показываем на месте блока «Турнир» тем, кто может им управлять; */}
            {/* по клику ведём в настройки, где владелец включает тумблер. */}
            {!event.tournamentsEnabled && effectiveEvent.canManageTournament && (
              <button
                onClick={() => navigate('/settings')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', textAlign: 'left',
                  background: '#fff', borderRadius: 14,
                  border: '1.5px dashed #fde68a',
                  boxShadow: '0 1px 8px rgba(0,0,0,0.05)', marginBottom: 12,
                  padding: '14px 16px', cursor: 'pointer',
                }}
              >
                {/* Пиктограмма кубка */}
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: '#fef3c7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="8 21 12 17 16 21"/>
                    <line x1="12" y1="17" x2="12" y2="11"/>
                    <path d="M7 4V3H17V4"/>
                    <path d="M7 4c0 5 5 8 5 8s5-3 5-8"/>
                    <path d="M5 4H3c0 5 2 7 4 8"/>
                    <path d="M19 4h2c0 5-2 7-4 8"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                    Подключить турниры?
                  </div>
                  <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>
                    Проводите турниры прямо в событии — включите модуль в настройках.
                  </div>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            )}

            {/* ── Roles Panel (collapsible) ── */}
            {effectiveEvent.canEdit && (
              <div style={{
                background: '#fff', borderRadius: 14,
                boxShadow: '0 1px 8px rgba(0,0,0,0.05)', marginBottom: 12,
                overflow: 'hidden',
              }}>
                {/* Collapsible header */}
                <button
                  onClick={() => setRolesOpen(v => !v)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 16px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>
                      Назначенные роли
                    </h2>
                  </div>
                  <IconChevron open={rolesOpen} />
                </button>

                {/* Panel content */}
                {rolesOpen && (
                  <div style={{ padding: '0 16px 14px' }}>
                    <RolesPanel event={event} />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Кнопка самостоятельной записи / отмены — фиксированный footer ── */}
      {!editMode && !participantEditMode && (() => {
        const isRegistered = !!event.selfRegistrationStatus;
        const isWaiting = event.selfRegistrationStatus === 'WAITING';
        return (
          <div style={{
            flexShrink: 0,
            padding: '10px 16px 14px',
            background: '#f8fafc',
            borderTop: '1px solid #f0f0f5',
          }}>
            <button
              onClick={handleSelfToggle}
              disabled={selfLoading}
              style={{
                width: '100%',
                padding: '13px 16px',
                borderRadius: 10,
                border: isRegistered ? '1.5px solid #fecaca' : 'none',
                background: selfLoading
                  ? '#f3f4f6'
                  : isRegistered
                    ? '#fef2f2'
                    : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: selfLoading
                  ? '#9ca3af'
                  : isRegistered ? '#dc2626' : '#fff',
                fontSize: 15,
                fontWeight: 700,
                cursor: selfLoading ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.15s',
              }}
            >
              {selfLoading
                ? 'Сохранение...'
                : isRegistered
                  ? (isWaiting ? 'Отменить запись (в очереди)' : 'Отменить запись')
                  : 'Записаться'}
            </button>
          </div>
        );
      })()}

      {/* ── Диалог подтверждения отмены собственной записи ── */}
      {showUnregisterConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '24px 20px',
            width: '100%', maxWidth: 320,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            animation: 'popIn 0.2s cubic-bezier(0.175,0.885,0.32,1.275)',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: '#fee2e2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, color: '#111827', textAlign: 'center' }}>
              Отменить запись
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.6 }}>
              Вы уверены, что хотите отменить свою запись на это событие?
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowUnregisterConfirm(false)}
                style={{
                  flex: 1, padding: '11px 16px',
                  borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: '#f9fafb', color: '#374151',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Назад
              </button>
              <button
                onClick={() => {
                  setShowUnregisterConfirm(false);
                  doSelfAction(false);
                }}
                style={{
                  flex: 2, padding: '11px 16px',
                  borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Да, отменить
              </button>
            </div>
          </div>
          <style>{`@keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        </div>
      )}

      {/* ── Диалог подтверждения повторной публикации ── */}
      {showRepublishConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '24px 20px',
            width: '100%', maxWidth: 320,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            animation: 'popIn 0.2s cubic-bezier(0.175,0.885,0.32,1.275)',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: '#fef3c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, color: '#111827', textAlign: 'center' }}>
              Повторная публикация
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.6 }}>
              Это событие уже было опубликовано в данный чат. Опубликовать повторно?
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowRepublishConfirm(false)}
                style={{
                  flex: 1, padding: '11px 16px',
                  borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: '#f9fafb', color: '#374151',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  setShowRepublishConfirm(false);
                  doPublish();
                }}
                style={{
                  flex: 2, padding: '11px 16px',
                  borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Да, опубликовать
              </button>
            </div>
          </div>
          <style>{`@keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        </div>
      )}
    </div>
  );
};

export default ViewEventPage;